import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PlayerContext, usePlayer } from "@/components/contexts/playerStore";
import { VideoModal } from "@/components/VideoModal";
import { GlobalVideoPlayer } from "@/components/player/GlobalVideoPlayer";
import { setPipEligible } from "@/lib/native";
import { useAudio } from "@/components/contexts/AudioContext";

// Re-exported so the existing `from "@/components/contexts/PlayerContext"` imports
// across the pages keep working; the hook itself lives in the leaf module.
export { usePlayer };

/**
 * Owns the one and only video player in the app.
 *
 * The important property here is that the embed survives everything: opening a
 * modal, navigating to the video page, scrolling the player out of view, leaving
 * for another route. A YouTube embed is an iframe, and an iframe that is unmounted
 * or reparented reloads from zero, so there is exactly one <iframe> in the tree -
 * it lives in GlobalVideoPlayer, is positioned `fixed`, and is animated over
 * whichever slot currently wants it. Pages and the modal never render a player;
 * they render a PlayerSlot, which is an empty box that reserves the layout and
 * says "put it here".
 *
 * Three modes:
 *   closed - nothing playing, no iframe
 *   docked - sitting over the registered slot, full chrome, page or modal
 *   mini   - a draggable window in the corner that outlives the page it came from
 */
export const PlayerProvider = ({ children }) => {
  const [active, setActive] = useState(null);
  const [mode, setMode] = useState("closed");
  const [surface, setSurface] = useState("modal");
  const [slot, setSlot] = useState(null);
  const { claimExclusivePlayback } = useAudio();
  const navigate = useNavigate();

  // setSlot is async, and the demotion below has to know whether a replacement has
  // arrived *this tick*, so the element is mirrored somewhere readable at once.
  const slotRef = useRef(null);
  const demoteTimer = useRef(null);

  /**
   * Open a video, or restore one that is already loaded.
   *
   * The identity check is the whole reason minimising works: handing back the same
   * `active` object leaves the iframe's key untouched, so React keeps the existing
   * element and the video carries on from where it was. Replacing it with an equal
   * but distinct object would reload the embed and restart playback.
   */
  const play = useCallback((video, { surface: nextSurface = "modal" } = {}) => {
    if (!video) return;
    setSurface(nextSurface);
    setActive((current) => (current && current.id === video.id ? current : video));
    setMode("docked");
  }, []);

  // The slot registration is deliberately left alone: dismissing the mini player
  // while its page is still open has to leave that page able to start again, and
  // the slot's own poster is what offers it.
  const close = useCallback(() => {
    clearTimeout(demoteTimer.current);
    setMode("closed");
    setActive(null);
  }, []);

  const minimize = useCallback(() => {
    setMode((m) => (m === "closed" ? m : "mini"));
  }, []);

  /** Return to the slot without moving the page - used when scrolling re-reveals it. */
  const dock = useCallback(() => {
    if (!slotRef.current) return;
    setMode((m) => (m === "closed" ? m : "docked"));
  }, []);

  const expand = useCallback(() => {
    if (!active) return;
    // Coming back to a page player from another route: there is no slot to dock to
    // until that page is mounted, so route there and let its slot promote us.
    if (surface === "page" && !slotRef.current) {
      navigate(`/video/${active.id}`);
      return;
    }
    // The slot may be scrolled off-screen - that is how the sticky mini player is
    // triggered in the first place - so bring it back before docking, or the
    // player would re-minimise on the very next frame.
    slotRef.current?.scrollIntoView?.({ behavior: "smooth", block: "center" });
    setMode("docked");
  }, [active, surface, navigate]);

  /**
   * A slot claims the player for as long as it is mounted.
   *
   * The delay on release is for /video/a -> /video/b: React unmounts the old slot
   * before mounting the new one, and demoting to the mini player in that gap would
   * flash a corner window on every navigation between two video pages.
   */
  const registerSlot = useCallback((el) => {
    if (!el) return () => {};
    clearTimeout(demoteTimer.current);
    slotRef.current = el;
    setSlot(el);
    setMode((m) => (m === "closed" ? m : "docked"));

    return () => {
      if (slotRef.current !== el) return;
      slotRef.current = null;
      setSlot(null);
      clearTimeout(demoteTimer.current);
      demoteTimer.current = setTimeout(() => {
        if (slotRef.current) return;
        setMode((m) => (m === "docked" ? "mini" : m));
      }, 80);
    };
  }, []);

  useEffect(() => () => clearTimeout(demoteTimer.current), []);

  // In the packaged app, leaving with a video open shrinks into a PiP window so
  // playback continues. The mini player counts: it is still playing, so pressing
  // Home from it should pop out rather than stop.
  useEffect(() => {
    setPipEligible(mode !== "closed");
  }, [mode]);

  // Silence the feed for as long as anything is playing here, mini included -
  // otherwise a card scrolled into view starts its own soundtrack underneath.
  useEffect(() => {
    if (mode === "closed") return;
    return claimExclusivePlayback();
  }, [mode, claimExclusivePlayback]);

  // "Up next" swaps the current video rather than stacking players.
  useEffect(() => {
    const onPlayRequest = (e) => e.detail && setActive(e.detail);
    window.addEventListener("tz-play-video", onPlayRequest);
    return () => window.removeEventListener("tz-play-video", onPlayRequest);
  }, []);

  const value = useMemo(
    () => ({ active, mode, surface, slot, play, close, minimize, expand, dock, registerSlot }),
    [active, mode, surface, slot, play, close, minimize, expand, dock, registerSlot]
  );

  return (
    <PlayerContext.Provider value={value}>
      {children}
      {active && mode === "docked" && surface === "modal" && (
        <VideoModal video={active} onClose={close} onMinimize={minimize} />
      )}
      <GlobalVideoPlayer />
    </PlayerContext.Provider>
  );
};
